import React, { useEffect, useRef, useState } from 'react';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { formatTimestamp } from '../utils/date-utils';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import { buttonClass } from '../components/ui/Button';
import { Field, TextArea } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import { AuthorityChip, AuthorityPicker } from './Authority';
import PointsPanel, { type PointCounts } from './PointsPanel';
import type { NodePointCounts, PointsHost } from './pointsTypes';
import type { NodeColor, NodePatch, PlanMember, PlanNode, TreeSection } from './types';

/** Node colors are content, not surfaces: a tint on the heading and the swatch, nothing louder. */
export const NODE_SWATCH: Record<Exclude<NodeColor, 'none'>, string> = {
  gold: '#ffcc33',
  sky: '#82b7ce',
  moss: '#6f8f5b',
  plum: '#8f5b7a',
  rust: '#b5653d',
  slate: '#5b6b8f',
};
const COLOR_ORDER: NodeColor[] = ['none', 'gold', 'sky', 'moss', 'plum', 'rust', 'slate'];
const COLOR_NAME: Record<NodeColor, string> = {
  none: 'No color',
  gold: 'Gold',
  sky: 'Sky',
  moss: 'Moss',
  plum: 'Plum',
  rust: 'Rust',
  slate: 'Slate',
};

export const sectionDomId = (id: string) => `np-sec-${id}`;

/**
 * A ref for the control that opens an inline editor or picker. When `open`
 * goes from true to false (save, cancel, Escape), focus goes back to it. The
 * trigger may have been unmounted while open, so this runs after the re-render
 * that brings it back.
 */
export function useReturnFocus<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) ref.current?.focus();
    wasOpen.current = open;
  }, [open]);
  return ref;
}

const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';
const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

export const ROW_BUTTON = buttonClass('quiet', 'sm');
export const DANGER_ROW_BUTTON = buttonClass('danger-quiet', 'sm');
/** A quiet status pill: Locked and Archived in the header, Done on a topic. */
export const PILL = 'inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2.5 py-0.5 text-xs font-medium text-on-surface-variant';
export const TONAL_SMALL = buttonClass('tonal', 'sm');
// Hidden until the row is hovered or holds focus; always shown where there is no hover.
export const HOVER_REVEAL =
  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';
/** Height grows from nothing: the wrapper animates grid rows, the child clips. Pair with DISCLOSURE_INNER. */
export const disclosure = (open: boolean) =>
  `grid transition-[grid-template-rows,visibility] duration-200 ease-emphasized motion-reduce:transition-none ${
    open ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'
  }`;
export const DISCLOSURE_INNER = 'min-h-0 overflow-hidden';
// HOVER_REVEAL scoped to the description, so the pencil answers that block and not the whole section.
const DESC_REVEAL =
  'opacity-0 group-hover/desc:opacity-100 group-focus-within/desc:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-150 ease-emphasized motion-reduce:transition-none';

export const ICON_PATH = {
  close: 'M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12 19 17.6 17.6 19 12 13.4 6.4 19 5 17.6 10.6 12 5 6.4z',
  pencil: 'M4 17.2V20h2.8l9.9-9.9-2.8-2.8zM18.4 8.4l-2.8-2.8 1.5-1.5a1 1 0 0 1 1.4 0l1.4 1.4a1 1 0 0 1 0 1.4z',
  check: 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z',
  reopen: 'M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8z',
};
const Icon: React.FC<{ d: string; className?: string }> = ({ d, className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
    <path fill="currentColor" d={d} />
  </svg>
);

/**
 * The toast and error fallback for Mark as done and Reopen, the same in the document and the canvas.
 * The longer line only when the topic is about to disappear, that is, while Show done is off.
 */
export const doneCopy = (done: boolean, showDone: boolean) =>
  done
    ? { success: showDone ? 'Marked done' : 'Marked done. Show done brings it back.', fallback: 'Could not mark the topic done.' }
    : { success: 'Reopened', fallback: 'Could not reopen the topic.' };

interface NodeEditorProps {
  node: Pick<PlanNode, 'title' | 'body'>;
  /** Receives only the fields that changed: the API rejects an empty PATCH. */
  onSave: (patch: Partial<Pick<PlanNode, 'title' | 'body'>>) => Promise<boolean>;
  onCancel: () => void;
}

/** Inline title and body edit. Enter in the title saves, Ctrl/Cmd+Enter in the body saves, Escape cancels. */
export const NodeEditor: React.FC<NodeEditorProps> = ({ node, onSave, onCancel }) => {
  const [title, setTitle] = useState(node.title);
  const [body, setBody] = useState(node.body);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
    titleRef.current?.select();
  }, []);

  const save = async () => {
    const clean = title.trim();
    if (!clean || busy) return;
    const patch: Partial<Pick<PlanNode, 'title' | 'body'>> = {};
    if (clean !== node.title) patch.title = clean;
    if (body !== node.body) patch.body = body;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    setBusy(true);
    const ok = await onSave(patch);
    // On success the parent unmounts this editor; only a failure comes back here.
    if (!ok) setBusy(false);
  };

  const onKeyDown = (e: React.KeyboardEvent, isBody: boolean) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter' && (!isBody || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };

  return (
    <div className="space-y-2 py-1">
      <Field
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => onKeyDown(e, false)}
        maxLength={200}
        disabled={busy}
        aria-label="Topic title"
        className="font-semibold"
      />
      <TextArea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => onKeyDown(e, true)}
        disabled={busy}
        rows={3}
        aria-label="Topic description"
        placeholder="Describe this topic"
      />
      <div className="flex items-center gap-1">
        <button type="button" onClick={save} disabled={busy || !title.trim()} className={TONAL_SMALL}>
          Save
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={ROW_BUTTON}>
          Cancel
        </button>
        <span className="ml-2 hidden text-[11px] text-on-surface-variant sm:inline">Ctrl+Enter saves, Esc cancels</span>
      </div>
    </div>
  );
};

const ColorPicker: React.FC<{ open: boolean; value: NodeColor; onPick: (c: NodeColor) => void; onClose: () => void }> = ({
  open,
  value,
  onPick,
  onClose,
}) => (
  <div className={disclosure(open)} aria-hidden={!open}>
    <div className={DISCLOSURE_INNER}>
      {/* The padding keeps focus rings inside the clip. */}
      <div
        role="group"
        aria-label="Topic color"
        className="flex flex-wrap items-center gap-0.5 px-0.5 pb-0.5 pt-1"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        {COLOR_ORDER.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={value === c}
            aria-label={COLOR_NAME[c]}
            onClick={() => onPick(c)}
            className={`np-state flex h-8 w-9 items-center justify-center rounded-full ${MOTION} ${FOCUS}`}
          >
            <span
              aria-hidden="true"
              className={`block h-3 w-6 rounded-full ${c === 'none' ? 'bg-surface-container-highest' : ''} ${
                value === c ? 'ring-2 ring-on-surface ring-offset-2 ring-offset-surface' : ''
              }`}
              style={c === 'none' ? undefined : { backgroundColor: NODE_SWATCH[c] }}
            />
          </button>
        ))}
      </div>
    </div>
  </div>
);

/** The other end of one arrow, and the arrow's label ('' when it has none). */
export interface ArrowRef {
  id: string;
  label: string;
}

export interface DocSectionProps {
  section: TreeSection;
  node: PlanNode;
  /** A section's number and title, for the arrow links. */
  numberOf: (id: string) => string | undefined;
  titleOf: (id: string) => string;
  /** A done end of an arrow: its chip is muted. */
  doneOf?: (id: string) => boolean;
  /** Every arrow into this section, by its source, in document order. */
  from: ArrowRef[];
  /** Every arrow out of this section, by its target, in document order. */
  to: ArrowRef[];
  current: boolean;
  /** May change this node on a writable plan: a manager, or this node's authority. */
  canEdit: boolean;
  /** Manager on a writable plan: may set who holds this node. */
  canSetAuthority: boolean;
  members: PlanMember[];
  /** Who is reading and what the plan allows, for the points under this topic. */
  points: PointsHost;
  /**
   * This node's line from `points/summary`. Undefined while the summary is not loaded;
   * null once it is and the node has no points (the summary leaves those out).
   */
  pointCounts?: NodePointCounts | null;
  /** The description and notes are shown. Held by the document so one key can open or close every section. */
  open: boolean;
  onToggle: () => void;
  onSave: (patch: NodePatch) => Promise<boolean>;
  /** Marks the topic done, or reopens it. Offered on accepted topics to those who may edit them. */
  onSetDone?: (done: boolean) => Promise<boolean>;
  onDelete: () => Promise<void>;
  onJump: (id: string) => void;
}

const ARROW_LABEL_MAX = 24;
/** Pixels a section steps in per level of nesting. */
const DEPTH_INDENT = 20;

/** " (label)" after the other end's title, cut to fit the chip, with the whole label on hover. */
const arrowLabel = (label: string) => {
  if (!label) return null;
  const short = label.length > ARROW_LABEL_MAX ? `${label.slice(0, ARROW_LABEL_MAX - 1).trimEnd()}…` : label;
  return (
    <>
      {' '}
      <span title={label}>({short})</span>
    </>
  );
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const DocSection: React.FC<DocSectionProps> = ({
  section,
  node,
  numberOf,
  titleOf,
  doneOf,
  from,
  to,
  current,
  canEdit,
  canSetAuthority,
  members,
  points,
  pointCounts,
  open: pointsOpen,
  onToggle: togglePoints,
  onSave,
  onSetDone,
  onDelete,
  onJump,
}) => {
  const confirm = useConfirm();
  // The panel mounts on first open and stays, so closing animates and reopening needs no fetch.
  const [pointsMounted, setPointsMounted] = useState(pointsOpen);
  if (pointsOpen && !pointsMounted) setPointsMounted(true);
  const [liveCounts, setLiveCounts] = useState<PointCounts | null>(null);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const editTrigger = useReturnFocus<HTMLButtonElement>(editing);
  const colorTrigger = useReturnFocus<HTMLButtonElement>(picking);
  const authorityTrigger = useReturnFocus<HTMLButtonElement>(choosing);
  const headingId = `${sectionDomId(section.id)}-h`;
  const isGroup = section.kind === 'group';
  const noun = isGroup ? 'group' : 'topic';
  const done = !!node.done_at;
  const canMarkDone = canEdit && !isGroup && node.status === 'accepted' && !!onSetDone;

  useEffect(() => {
    if (!canEdit) {
      setEditing(false);
      setPicking(false);
    }
  }, [canEdit]);

  useEffect(() => {
    if (!canSetAuthority) setChoosing(false);
  }, [canSetAuthority]);

  const askDelete = async () => {
    const ok = await confirm(
      isGroup
        ? {
            title: 'Remove this group?',
            message: `"${node.title}" leaves the plan, along with its arrows. What it holds moves up a level.`,
            confirmLabel: 'Remove group',
            tone: 'danger',
          }
        : {
            title: 'Remove this topic?',
            message: `"${node.title}" leaves the plan, along with the arrows to and from it.`,
            confirmLabel: 'Remove topic',
            tone: 'danger',
          },
    );
    if (ok) await onDelete();
  };

  const pickColor = async (c: NodeColor) => {
    setPicking(false);
    if (c !== node.color) await onSave({ color: c });
  };

  const pickAuthority = async (userId: string | null) => {
    setChoosing(false);
    if (userId !== node.authority_id) await onSave({ authority_id: userId });
  };

  const authorityLabel = node.authority_id ? 'Change authority' : 'Set authority';

  // What the panel itself loaded beats the page's summary: it is newer.
  const counts: PointCounts | null =
    liveCounts ?? (pointCounts ? { points: pointCounts.points, votes: pointCounts.votes } : pointCounts === null ? { points: 0, votes: 0 } : null);
  const countsLabel = !counts
    ? ''
    : counts.points === 0
      ? 'No notes yet'
      : `${plural(counts.points, 'note', 'notes')}${counts.votes > 0 ? `, ${plural(counts.votes, 'vote', 'votes')}` : ''}`;
  const pointsRegion = `${sectionDomId(section.id)}-points`;
  const countsId = `${sectionDomId(section.id)}-count`;
  // A reader with nothing to add sees nothing where there is no description.
  const showDescription = !editing && (!!node.body || canEdit);

  const arrowChip = ({ id, label }: ArrowRef) => {
    const name = [numberOf(id), titleOf(id)].filter(Boolean).join(' ');
    return (
      // Inline-block, not flex: a flex chip drops the spaces between number, title and label.
      <Tooltip key={id} label={name}>
        <a
          href={`#${sectionDomId(id)}`}
          aria-label={`Go to ${name}${label ? ` (${label})` : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onJump(id);
          }}
          className={`np-state inline-block max-w-full truncate whitespace-nowrap rounded-full bg-surface-container-high px-2 text-[11px] font-medium leading-5 text-on-surface-variant hover:text-on-surface [@media(pointer:coarse)]:leading-8 ${MOTION} ${FOCUS}`}
        >
          {numberOf(id) && (
            <>
              <span className="font-mono tabular-nums">{numberOf(id)}</span>{' '}
            </>
          )}
          {doneOf?.(id) ? <span className="opacity-60">{titleOf(id)}</span> : titleOf(id)}
          {arrowLabel(label)}
        </a>
      </Tooltip>
    );
  };

  return (
    <section
      id={sectionDomId(section.id)}
      aria-labelledby={headingId}
      tabIndex={-1}
      className={`group np-state relative scroll-mt-24 rounded-lg py-2 pl-4 pr-2 focus:outline-none ${
        current ? 'bg-surface-container-low' : ''
      } ${MOTION}`}
      // pl-4 plus one step per level, so what a group holds sits under its heading.
      style={section.depth > 0 ? { paddingLeft: 16 + DEPTH_INDENT * section.depth } : undefined}
    >
      {editing ? (
        <NodeEditor
          node={node}
          onCancel={() => setEditing(false)}
          onSave={async (patch) => {
            const ok = await onSave(patch);
            if (ok) setEditing(false);
            return ok;
          }}
        />
      ) : (
        <>
          {/* The whole row opens the description and notes. The title's button is its keyboard stop (Enter and
              Space click it, and the click bubbles here); controls inside stop their own clicks. */}
          {/* Every row takes the tint's padding, so setting a color never shifts the row. */}
          <div
            onClick={togglePoints}
            className="relative -mx-2 flex cursor-pointer flex-wrap items-start gap-x-1 rounded-md px-2 py-1"
            style={
              node.color === 'none'
                ? undefined
                : { backgroundColor: `color-mix(in srgb, ${NODE_SWATCH[node.color]} 16%, transparent)` }
            }
          >
            <h2
              id={headingId}
              className={`min-w-0 flex-1 pt-1 ${isGroup ? 'text-[20px]' : 'text-[18px]'} font-semibold leading-snug text-on-surface`}
            >
              <button
                type="button"
                aria-expanded={pointsOpen}
                aria-controls={pointsRegion}
                aria-describedby={countsLabel ? countsId : undefined}
                // The ring is drawn on a pseudo-element spanning the row, so focus outlines the whole target.
                className="text-left after:pointer-events-none after:absolute after:inset-0 after:rounded-md focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-primary/50"
              >
                {section.number && (
                  <>
                    <span
                      className={`mr-1.5 font-mono text-[0.85em] font-medium tabular-nums text-on-surface-variant ${done ? 'opacity-60' : ''}`}
                    >
                      {section.number}
                    </span>{' '}
                  </>
                )}
                <span className={`break-words ${done ? 'text-on-surface-variant' : ''}`}>{node.title}</span>
                {done && ' '}
                <Appear show={!!done} className={`${PILL} ml-1.5 align-[3px]`}>
                  <Icon d={ICON_PATH.check} className="h-3.5 w-3.5" />
                  Done
                </Appear>
              </button>
            </h2>
            {canEdit && (
              // Under sm the actions wrap to their own line below the row. With hover, that line has no
              // height until the row is hovered or holds focus. max-h, not display:none, so Tab still reaches them.
              <div
                onClick={(e) => e.stopPropagation()}
                className={`flex flex-shrink-0 basis-full flex-wrap items-center max-sm:order-last sm:basis-auto ${
                  choosing ? 'opacity-100' : HOVER_REVEAL
                } max-sm:[@media(hover:hover)]:max-h-0 max-sm:[@media(hover:hover)]:overflow-hidden max-sm:[@media(hover:hover)]:group-hover:max-h-12 max-sm:[@media(hover:hover)]:group-focus-within:max-h-12`}
              >
                <Tooltip label="Color">
                  <button
                    type="button"
                    onClick={() => setPicking((p) => !p)}
                    ref={colorTrigger}
                    aria-expanded={picking}
                    className={ROW_BUTTON}
                    aria-label={`Color for ${node.title}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`block h-4 w-4 rounded-full ${node.color === 'none' ? 'ring-1 ring-inset ring-outline' : ''}`}
                      style={node.color === 'none' ? undefined : { backgroundColor: NODE_SWATCH[node.color] }}
                    />
                  </button>
                </Tooltip>
                {canSetAuthority && (
                  <button
                    type="button"
                    ref={authorityTrigger}
                    onClick={() => setChoosing((c) => !c)}
                    aria-haspopup="dialog"
                    aria-expanded={choosing}
                    aria-label={`${authorityLabel} for ${node.title}`}
                    className={ROW_BUTTON}
                  >
                    {authorityLabel}
                  </button>
                )}
                {canMarkDone && onSetDone && (
                  <Tooltip label={done ? 'Reopen' : 'Mark as done'}>
                    <button
                      type="button"
                      onClick={() => onSetDone(!done)}
                      className={ROW_BUTTON}
                      aria-label={`${done ? 'Reopen' : 'Mark as done:'} ${node.title}`}
                    >
                      <Icon d={done ? ICON_PATH.reopen : ICON_PATH.check} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip label={`Remove ${noun}`}>
                  <button
                    type="button"
                    onClick={askDelete}
                    className={DANGER_ROW_BUTTON}
                    aria-label={`Remove ${noun} ${node.title}`}
                  >
                    <Icon d={ICON_PATH.close} />
                  </button>
                </Tooltip>
              </div>
            )}
            <div className="ml-auto flex flex-shrink-0 flex-wrap items-center gap-1.5 pt-1.5">
              <AuthorityChip name={node.authority_name} />
              {countsLabel && (
                <span id={countsId} className="whitespace-nowrap text-xs tabular-nums text-on-surface-variant">
                  {countsLabel}
                </span>
              )}
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className={`h-4 w-4 flex-shrink-0 text-on-surface-variant transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${
                  pointsOpen ? 'rotate-180' : ''
                }`}
                fill="none"
              >
                <path fill="currentColor" d="M16.6 8.6 12 13.2 7.4 8.6 6 10l6 6 6-6z" />
              </svg>
            </div>
          </div>

          {canEdit && (
            <ColorPicker open={picking} value={node.color} onPick={pickColor} onClose={() => setPicking(false)} />
          )}
          {choosing && canSetAuthority && (
            <AuthorityPicker
              anchor={authorityTrigger}
              nodeTitle={node.title}
              members={members}
              currentId={node.authority_id}
              onPick={pickAuthority}
              onClose={() => setChoosing(false)}
            />
          )}
        </>
      )}

      {pointsMounted && (
        <div id={pointsRegion} className={disclosure(pointsOpen)} aria-hidden={!pointsOpen}>
          <div className={DISCLOSURE_INNER}>
            <div className={`max-w-[72ch] pb-2 ${showDescription ? 'pt-0.5' : 'pt-3'}`}>
              {showDescription && (
                <div className="group/desc mb-3 flex items-start gap-1">
                  {node.body ? (
                    <p className="min-w-0 max-w-[65ch] flex-1 whitespace-pre-wrap break-words pt-1.5 text-[13px] leading-relaxed text-on-surface-variant">
                      {node.body}
                    </p>
                  ) : (
                    // The words are the pointer shortcut; the pencil is the keyboard path.
                    <p
                      onClick={() => setEditing(true)}
                      className="min-w-0 flex-1 cursor-text pt-1.5 text-[13px] leading-relaxed text-on-surface-variant"
                    >
                      Add a description
                    </p>
                  )}
                  {canEdit && (
                    <Tooltip label="Edit">
                      <button
                        ref={editTrigger}
                        type="button"
                        onClick={() => setEditing(true)}
                        className={`${ROW_BUTTON} flex-shrink-0 ${DESC_REVEAL}`}
                        aria-label={`Edit ${node.title}`}
                      >
                        <Icon d={ICON_PATH.pencil} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
              <Reveal show={!!done}>
                {done && (
                  <p className="mb-3 text-xs text-on-surface-variant">
                    {node.done_by_name ? `Done by ${node.done_by_name}` : 'Done'} on {formatTimestamp(node.done_at)}
                  </p>
                )}
              </Reveal>
              {/* Top-aligned with matching line heights: a truncated chip's baseline is its bottom edge. */}
              {(from.length > 0 || to.length > 0) && (
                <dl className="mb-3 grid grid-cols-[max-content_minmax(0,1fr)] items-start gap-x-3 gap-y-1.5">
                  {from.length > 0 && (
                    <>
                      <dt className="text-xs leading-5 text-on-surface-variant [@media(pointer:coarse)]:leading-8">Comes from</dt>
                      <dd className="flex min-w-0 flex-wrap gap-1.5">{from.map(arrowChip)}</dd>
                    </>
                  )}
                  {to.length > 0 && (
                    <>
                      <dt className="text-xs leading-5 text-on-surface-variant [@media(pointer:coarse)]:leading-8">Goes to</dt>
                      <dd className="flex min-w-0 flex-wrap gap-1.5">{to.map(arrowChip)}</dd>
                    </>
                  )}
                </dl>
              )}
              <PointsPanel
                planId={points.planId}
                nodeId={node.id}
                role={points.role}
                userId={points.userId}
                authorityId={node.authority_id}
                locked={points.locked}
                archived={points.archived}
                members={members}
                seq={node.seq}
                onChanged={points.onChanged}
                onCounts={setLiveCounts}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DocSection;
